import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    MenuItem,
    Button,
    Chip,
    TablePagination,
    Tooltip,
    Menu
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { PayrollService } from '../../services/payroll.service';
import { useToast } from '../../context/ToastContext';
import { usePayrollInstitution } from '../../hooks/usePayrollInstitution';
import { IPayrollBatchApproval } from '../../interfaces/payroll.interface';
import { ISalaryPaymentBatch, MfsFileFormat, SalaryPaymentBatchStatus } from '../../interfaces/salaryPayment';
import MfsDisbursementPreviewDialog from './MfsDisbursementPreviewDialog';
import SearchableSelect from '../common/SearchableSelect';
import { humanizeEnum } from '../../utils/format';

const MFS_FORMAT_LABELS: Record<MfsFileFormat, string> = {
    BKASH: 'bKash',
    NAGAD: 'Nagad',
    GENERIC: 'Generic',
};
const MFS_FORMATS: MfsFileFormat[] = ['BKASH', 'NAGAD', 'GENERIC'];

const STATUS_COLORS: Record<SalaryPaymentBatchStatus, 'success' | 'default' | 'warning'> = {
    PENDING: 'default',
    COMPLETED: 'success',
    VOIDED: 'default',
    PARTIALLY_VOIDED: 'warning',
};
const STATUS_LABELS: Record<SalaryPaymentBatchStatus, string> = {
    PENDING: 'Pending',
    COMPLETED: 'Completed',
    VOIDED: 'Voided',
    PARTIALLY_VOIDED: 'Partly voided',
};

// B-44: approval state of the payroll month(s) a batch pays, as a chip.
const APPROVAL_CHIPS: Record<IPayrollBatchApproval['state'], { label: string; color: 'success' | 'warning' | 'error' | 'default' }> = {
    APPROVED: { label: 'Approved', color: 'success' },
    NOT_APPROVED: { label: 'Not approved', color: 'warning' },
    CHANGED: { label: 'Changed after approval', color: 'error' },
    BLOCKED: { label: 'Blocked', color: 'error' },
    NO_PAYROLL: { label: 'No payroll run', color: 'default' },
};

const isMissing = (v?: string | null) => !v || v.trim() === '';

// Count BANK_TRANSFER, non-reversed payments in a batch that lack bank account
// info the frontend can see. (Routing/branch live on the User master and are
// validated authoritatively by the CSV endpoint — surfaced as trailing
// "# Warning:" comment lines inside the downloaded file.)
const missingBankCount = (batch: ISalaryPaymentBatch): number =>
    batch.payments.filter(p =>
        p.status !== 'REVERSED' && (isMissing(p.bankName) || isMissing(p.bankAccountNumber))
    ).length;

const PaymentBatchList: React.FC = () => {
    const [batches, setBatches] = useState<ISalaryPaymentBatch[]>([]);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(25);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(false);
    // Approval state per batch id. Missing entry = unknown (state not loaded): downloads stay enabled
    // and the server still refuses with a 409 if the run is not approved.
    const [approvals, setApprovals] = useState<Record<string, IPayrollBatchApproval>>({});
    const { showApiError } = useToast();
    const { isSystemAdmin, institutions, institutionId, setInstitutionId } = usePayrollInstitution();

    // MFS disbursement lane (spec §2, "PaymentBatchList.tsx"): a split button per MOBILE_BANKING
    // batch that opens a format menu, then a preview dialog with a "Download CSV" action.
    const [mfsAnchorEl, setMfsAnchorEl] = useState<HTMLElement | null>(null);
    const [mfsMenuBatchId, setMfsMenuBatchId] = useState<string | null>(null);
    const [mfsPreviewBatchId, setMfsPreviewBatchId] = useState<string | null>(null);
    const [mfsPreviewFormat, setMfsPreviewFormat] = useState<MfsFileFormat>('BKASH');
    const [mfsPreviewOpen, setMfsPreviewOpen] = useState(false);

    const load = useCallback(async () => {
        if (!institutionId) return;
        setLoading(true);
        try {
            const res = await PayrollService.listBatches(institutionId, page, size);
            setBatches(res.content);
            setTotalElements(res.totalElements);
            // One call for the whole page of batches; a failure only loses the badges and tooltips.
            try {
                const states = await PayrollService.getBatchApprovalStates(res.content.map(b => b.id), institutionId);
                setApprovals(Object.fromEntries((states ?? []).map(a => [a.batchId, a])));
            } catch {
                setApprovals({});
            }
        } catch (error) {
            showApiError(error, 'Failed to load payment batches');
        } finally {
            setLoading(false);
        }
    }, [institutionId, page, size, showApiError]);

    useEffect(() => {
        load();
    }, [load]);

    const handleDownloadCsv = async (batchId: string) => {
        try {
            await PayrollService.downloadBankAdviceCsv(batchId);
        } catch (error) {
            showApiError(error, 'Failed to download bank advice CSV');
        }
    };

    const openMfsMenu = (event: React.MouseEvent<HTMLElement>, batchId: string) => {
        setMfsAnchorEl(event.currentTarget);
        setMfsMenuBatchId(batchId);
    };

    const closeMfsMenu = () => {
        setMfsAnchorEl(null);
        setMfsMenuBatchId(null);
    };

    const chooseMfsFormat = (format: MfsFileFormat) => {
        if (mfsMenuBatchId) {
            setMfsPreviewBatchId(mfsMenuBatchId);
            setMfsPreviewFormat(format);
            setMfsPreviewOpen(true);
        }
        closeMfsMenu();
    };

    return (
        <Box sx={{ p: 3 }} data-help-page="payroll.batches.page">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <Typography variant="h5">Payment Batches</Typography>
                <Box sx={{ flexGrow: 1 }} />
                {isSystemAdmin && (
                    <SearchableSelect
                        data-help="payroll.batches.institution"
                        label="Select Institution"
                        options={institutions}
                        value={institutionId}
                        onChange={id => setInstitutionId(id)}
                        getOptionLabel={inst => inst.name}
                        clearable={false}
                        sx={{ minWidth: 220 }}
                    />
                )}
            </Box>

            <TableContainer component={Paper} data-help-section="payroll.batches.table">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="payroll.batches.col.date">Batch Date</TableCell>
                            <TableCell data-help="payroll.batches.col.month">Payroll Month</TableCell>
                            <TableCell data-help="payroll.batches.col.method">Method</TableCell>
                            <TableCell data-help="payroll.batches.col.reference">Reference</TableCell>
                            <TableCell align="right" data-help="payroll.batches.col.payments">Payments</TableCell>
                            <TableCell data-help="payroll.batches.col.status">Status</TableCell>
                            <TableCell data-help="payroll.batches.col.approval">Approval</TableCell>
                            <TableCell align="right" data-help="payroll.batches.col.file">Disbursement File</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={8} align="center">Loading...</TableCell>
                            </TableRow>
                        )}
                        {!loading && batches.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={8} align="center">No payment batches found</TableCell>
                            </TableRow>
                        )}
                        {!loading && batches.map(batch => {
                            const isBankTransfer = batch.paymentMethod === 'BANK_TRANSFER';
                            const isMobileBanking = batch.paymentMethod === 'MOBILE_BANKING';
                            const missing = isBankTransfer ? missingBankCount(batch) : 0;
                            const approval = approvals[batch.id];
                            const blockedReason = approval && !approval.exportable
                                ? (approval.reason ?? 'The payroll run is not approved.')
                                : '';
                            return (
                                <TableRow key={batch.id}>
                                    <TableCell>{batch.batchDate}</TableCell>
                                    <TableCell>{approval?.periodLabel ?? '-'}</TableCell>
                                    <TableCell>{humanizeEnum(batch.paymentMethod)}</TableCell>
                                    <TableCell>{batch.reference || '-'}</TableCell>
                                    <TableCell align="right">
                                        {batch.completedPayments}/{batch.totalPayments}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            data-help="payroll.batches.status"
                                            size="small"
                                            label={STATUS_LABELS[batch.status] ?? batch.status}
                                            color={STATUS_COLORS[batch.status]}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {approval ? (
                                            <Chip
                                                data-help="payroll.batches.approval"
                                                size="small"
                                                label={APPROVAL_CHIPS[approval.state]?.label ?? approval.state}
                                                color={APPROVAL_CHIPS[approval.state]?.color ?? 'default'}
                                            />
                                        ) : '-'}
                                    </TableCell>
                                    <TableCell align="right">
                                        {isBankTransfer ? (
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-end' }}>
                                                {missing > 0 && (
                                                    <Tooltip title="Some rows are missing bank account info; the CSV lists them with a warning.">
                                                        <Chip
                                                            data-help="payroll.batches.missingBank"
                                                            size="small"
                                                            color="warning"
                                                            icon={<WarningAmberIcon />}
                                                            label={`${missing} row(s) missing bank data`}
                                                        />
                                                    </Tooltip>
                                                )}
                                                <Tooltip title={blockedReason}>
                                                    <span data-help="payroll.batches.bankAdvice">
                                                        <Button
                                                            size="small"
                                                            startIcon={<DownloadIcon />}
                                                            onClick={() => handleDownloadCsv(batch.id)}
                                                            disabled={!!blockedReason}
                                                        >
                                                            Bank Advice (CSV)
                                                        </Button>
                                                    </span>
                                                </Tooltip>
                                            </Box>
                                        ) : isMobileBanking ? (
                                            <Tooltip title={blockedReason}>
                                                <span data-help="payroll.batches.mfsFile">
                                                    <Button
                                                        size="small"
                                                        endIcon={<ArrowDropDownIcon />}
                                                        onClick={(e) => openMfsMenu(e, batch.id)}
                                                        disabled={!!blockedReason}
                                                    >
                                                        MFS File
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">
                                                N/A (no disbursement file)
                                            </Typography>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={totalElements}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={size}
                    onRowsPerPageChange={(e) => { setSize(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                />
            </TableContainer>

            <Menu anchorEl={mfsAnchorEl} open={Boolean(mfsAnchorEl)} onClose={closeMfsMenu}>
                {MFS_FORMATS.map((format) => (
                    <MenuItem key={format} onClick={() => chooseMfsFormat(format)}>
                        {MFS_FORMAT_LABELS[format]}
                    </MenuItem>
                ))}
            </Menu>

            <MfsDisbursementPreviewDialog
                open={mfsPreviewOpen}
                batchId={mfsPreviewBatchId}
                format={mfsPreviewFormat}
                onClose={() => setMfsPreviewOpen(false)}
            />
        </Box>
    );
};

export default PaymentBatchList;
